"use client"

import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { replacePlaceholdersInItemInfoBlocks } from '@/lib/utils/replace-placeholders';
import Image from 'next/image';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

interface ItemInfoBlock {
  S_TITLE: string;
  S_DESK: string;
  S_FILE: {
    src: string;
    width: number;
    height: number;
    alt: string;
  } | null;
  S_TEMPLATE: 'img_text' | 'text_img';
}

interface ItemInfoBlocksProps {
  className?: string;
  blocks?: ItemInfoBlock[];
  branchCode?: string;
}

function ItemInfoBlockContent({ block, index }: { block: ItemInfoBlock; index: number }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const innerContentRef = useRef<HTMLDivElement>(null);
  const isImageFirst = block.S_TEMPLATE === 'img_text';
  const hasImage = Boolean(block.S_FILE?.src);

  useEffect(() => {
    const checkOverflow = () => {
      if (innerContentRef.current) {
        const inner = innerContentRef.current;
        
        // Ждем, пока контент загрузится
        if (inner.scrollHeight === 0) {
          return;
        }
        
        // Получаем реальную высоту внутреннего контента
        const innerHeight = inner.scrollHeight;
        
        // Определяем максимальную высоту в зависимости от размера экрана
        const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
        const maxHeight = isDesktop ? 350 : 250;
        
        // Проверяем, превышает ли внутренний контент максимальную высоту
        // Добавляем небольшой отступ (2px) для учета погрешностей округления
        const isOverflow = innerHeight > maxHeight + 2;
        
        setIsOverflowing(isOverflow);
      }
    };

    // Проверяем при монтировании и изменении размера окна
    checkOverflow();
    
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleResize = () => {
      checkOverflow();
    };
    const handleMediaChange = () => {
      checkOverflow();
    };
    
    window.addEventListener('resize', handleResize);
    mediaQuery.addEventListener('change', handleMediaChange);
    
    // Используем MutationObserver для отслеживания изменений в DOM
    let observer: MutationObserver | null = null;
    const imageHandlers: Array<{ img: HTMLImageElement; loadHandler: () => void; errorHandler: () => void }> = [];
    
    if (innerContentRef.current) {
      observer = new MutationObserver(() => {
        checkOverflow();
        // При изменении DOM перепроверяем изображения
        if (innerContentRef.current) {
          const images = innerContentRef.current.querySelectorAll('img');
          images.forEach((img) => {
            if (!img.complete && !imageHandlers.find(h => h.img === img)) {
              const loadHandler = () => checkOverflow();
              const errorHandler = () => checkOverflow();
              img.addEventListener('load', loadHandler, { once: true });
              img.addEventListener('error', errorHandler, { once: true });
              imageHandlers.push({ img, loadHandler, errorHandler });
            }
          });
        }
      });
      observer.observe(innerContentRef.current, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
      
      // Также отслеживаем загрузку изображений внутри контента
      const images = innerContentRef.current.querySelectorAll('img');
      images.forEach((img) => {
        if (!img.complete) {
          const loadHandler = () => checkOverflow();
          const errorHandler = () => checkOverflow();
          img.addEventListener('load', loadHandler, { once: true });
          img.addEventListener('error', errorHandler, { once: true });
          imageHandlers.push({ img, loadHandler, errorHandler });
        }
      });
    }
    
    // Также проверяем после задержки, чтобы дать время на рендеринг HTML контента
    const timeoutIds = [
      setTimeout(checkOverflow, 100),
      setTimeout(checkOverflow, 300),
      setTimeout(checkOverflow, 500),
      setTimeout(checkOverflow, 1000),
      setTimeout(checkOverflow, 2000),
    ];

    return () => {
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleMediaChange);
      if (observer) {
        observer.disconnect();
      }
      imageHandlers.forEach(({ img, loadHandler, errorHandler }) => {
        img.removeEventListener('load', loadHandler);
        img.removeEventListener('error', errorHandler);
      });
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [block.S_DESK]);

  return (
    <section key={index} className={cn('py-8 sm:py-16')}>
      <div className="w-full">
        <div className={cn(
          "grid gap-6 sm:gap-8 lg:gap-12",
          hasImage ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1"
        )}>
          <div className="order-1 lg:hidden">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900">
              {block.S_TITLE}
            </h2>
          </div>

          {/* Изображение */}
          {hasImage && block.S_FILE && (
            <div 
              className={cn(
                "w-full h-[300px] lg:h-[500px] rounded-lg overflow-hidden relative border border-slate-200",
                isImageFirst ? "order-2 lg:order-1" : "order-2 lg:order-2"
              )}
            >
              <Image
                src={block.S_FILE.src}
                alt={block.S_FILE.alt || block.S_TITLE}
                width={block.S_FILE.width || 1536}
                height={block.S_FILE.height || 1024}
                className="w-full h-full object-cover"
                sizes="(max-width: 1024px) 100vw, 50vw"
                quality={75}
              />
            </div>
          )}

          {/* Текст */}
          <div 
            className={cn(
              "flex flex-col justify-center",
              hasImage
                ? (isImageFirst ? "order-3 lg:order-2" : "order-3 lg:order-1")
                : "order-2"
            )}
          >
            <h2 className="hidden text-2xl sm:text-3xl font-semibold text-slate-900 mb-6 sm:mb-8 lg:block">
              {block.S_TITLE}
            </h2>
            
            {/* Текст с ограничением высоты - весь контент в DOM для SEO */}
            <div className="relative">
              <div 
                ref={contentRef}
                className="overflow-hidden max-h-[250px] lg:max-h-[350px]"
              >
                <div 
                  ref={innerContentRef}
                    className="cms-richtext min-w-0 space-y-4 sm:space-y-6 text-sm sm:text-base text-slate-700 leading-relaxed"
                >
                  <div 
                    dangerouslySetInnerHTML={{ __html: block.S_DESK }}
                  />
                </div>
              </div>
              
              {/* Градиент для плавного перехода - показываем только если текст обрезан */}
              {isOverflowing && (
                <div 
                  className="absolute bottom-12 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent pointer-events-none"
                />
              )}
              
              {/* Кнопка показать полностью - показываем только если текст обрезан */}
              {isOverflowing && (
                <Dialog open={isOpen} onOpenChange={setIsOpen}>
                  <DialogTrigger asChild>
                    <Button
                      variant="outline"
                      className="mt-4 w-full sm:w-auto relative z-10"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsOpen(true);
                      }}
                    >
                      Показать полностью
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
                    <DialogHeader>
                      <DialogTitle>{block.S_TITLE}</DialogTitle>
                    </DialogHeader>
                    <div 
                      className="cms-richtext min-w-0 overflow-y-auto flex-1 space-y-4 sm:space-y-6 text-sm sm:text-base text-slate-700 leading-relaxed pr-2"
                      style={{
                        maxHeight: 'calc(90vh - 120px)',
                      }}
                    >
                      <div 
                        dangerouslySetInnerHTML={{ __html: block.S_DESK }}
                      />
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function ItemInfoBlocks({
  className,
  blocks = [],
  branchCode = 'spb',
}: ItemInfoBlocksProps) {
  const processedBlocks = replacePlaceholdersInItemInfoBlocks(blocks, branchCode);

  // Если нет блоков, не показываем компонент
  if (!processedBlocks || processedBlocks.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {processedBlocks.map((block, index) => (
        <ItemInfoBlockContent key={index} block={block} index={index} />
      ))}
    </div>
  );
}
